/**
 * particulier.ui.ts
 * ─────────────────────────────────────────────────────────────────
 * Tokens UI riches du profil "Particuliers".
 *
 * Source unique pour :
 *  - les classes Tailwind composées (halos, gradients, surfaces dark)
 *  - les textes récurrents (bullets de réassurance, microPromise, badges)
 *  - les configurations d'animation (delays staggerés)
 *
 * Consommé exclusivement par les composants `client/src/components/audience/particulier/*`
 * — NE PAS importer ailleurs.
 */

import type { ReactNode } from 'react';

/* ────────────────────────────────────────────────────────────────
 * 1. Couleurs & gradients (tokens dérivés palette V4)
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_GRADIENT = {
  /** Headline gradient — brand vers accent (côte à côte) */
  headline:
    'bg-gradient-to-r from-brand-600 via-brand-500 to-accent-500 bg-clip-text text-transparent',
  /** Headline dark variant — pour CTA finals fond noir */
  headlineDark:
    'bg-gradient-to-br from-accent-300 via-brand-300 to-brand-200 bg-clip-text text-transparent',
  /** Halo derrière side card du hero */
  sideCardHalo:
    'bg-gradient-to-br from-brand-100/40 via-accent-100/30 to-transparent',
  /** Icône halo gradient (background des icônes en cards) */
  iconHalo:
    'bg-gradient-to-br from-brand-50 to-brand-100/40',
  /** Numero step gradient (dot du stepper) */
  stepDot:
    'bg-gradient-to-br from-brand-500 to-brand-700',
  /** Aide value gradient (montants des aides) */
  aideAmount:
    'bg-gradient-to-br from-accent-700 to-brand-600 bg-clip-text text-transparent',
  /** Card top hairline accent */
  cardTopHairline:
    'bg-gradient-to-r from-transparent via-brand-300/50 to-transparent',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 2. Halo / glow / surfaces (backgrounds décoratifs)
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_HALOS = {
  /** Halo top-right brand (hero) */
  topRight:
    'absolute -right-32 -top-32 h-[480px] w-[480px] rounded-full bg-brand-100/40 blur-[120px]',
  /** Halo bottom-left accent (hero) */
  bottomLeft:
    'absolute -left-24 bottom-[-180px] h-[420px] w-[420px] rounded-full bg-accent-100/40 blur-[120px]',
  /** Halo top-right pour CTA dark */
  ctaTopRight:
    'pointer-events-none absolute -right-32 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/30 blur-[140px]',
  /** Halo bottom-left pour CTA dark */
  ctaBottomLeft:
    'pointer-events-none absolute -left-24 bottom-[-120px] h-[420px] w-[420px] rounded-full bg-brand-500/30 blur-[120px]',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 3. Section paddings standardisés
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_SECTION = {
  /** Section standard (entre hero et CTA) */
  standard: 'py-20 md:py-28',
  /** Section secondaire (densité plus forte) */
  compact: 'py-16 md:py-20',
  /** Hero section padding */
  hero: 'pt-12 pb-16 md:pt-20 md:pb-24',
  /** KPI band padding */
  kpiBand: 'py-10',
  /** Section sur fond alterné (stone-50) */
  alternateBg: 'border-t border-stone-100 bg-gradient-to-b from-stone-50/60 to-paper',
  /** Section sur fond léger compact */
  alternateBgCompact: 'border-y border-stone-200/60 bg-stone-50/50',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 4. Surfaces de cards récurrentes
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_CARD = {
  /** Card standard cliquable avec hover lift */
  interactive:
    'group relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.12)]',
  /** Card statique (sans hover) */
  static:
    'relative h-full overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)]',
  /** Card compacte (plus petite) */
  compact:
    'group flex h-full flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.10)]',
  /** Pictogramme dans card (icône halo gradient) */
  iconWrapper:
    'inline-flex items-center justify-center rounded-xl text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-200 ease-out group-hover:scale-105',
  /** Pictogramme size sm */
  iconSm: 'h-9 w-9',
  /** Pictogramme size md */
  iconMd: 'h-11 w-11',
  /** Pictogramme size lg */
  iconLg: 'h-12 w-12',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 5. Pills/badges récurrents
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_PILL = {
  /** Pill eyebrow standard (light) */
  eyebrow:
    'inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur',
  /** Pill eyebrow CTA dark */
  eyebrowDark:
    'inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory-50 backdrop-blur-md',
  /** Mini chip de catégorie sur card */
  categoryChip:
    'rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500',
  /** Mini badge avec dot pulse */
  livePulse:
    'inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 6. Copies récurrentes (réassurance, micro-promises)
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_COPY = {
  /** Bullets de réassurance hero standard (light) */
  reassuranceBullets: [
    'Gratuit, sans inscription',
    'Sources officielles citées',
    'Aucune relance commerciale',
  ] as string[],
  /** Bullets de réassurance CTA dark */
  reassuranceDarkBullets: [
    'Aucune relance',
    'Sources officielles',
    'Résultat immédiat',
  ] as string[],
  /** Micro-promise principale (sous CTAs) */
  microPromise: 'Réponse chiffrée immédiate, sans inscription',
  /** Badge CTA final */
  ctaFinalBadge: 'Calculateur 60 sec, gratuit',
  /** Footnote CTA final */
  ctaFinalFootnote: 'Aucune carte bancaire, aucune inscription, aucun spam',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 7. URLs canoniques
 * ──────────────────────────────────────────────────────────────── */

export const PARTICULIER_URLS = {
  site: 'https://solutionecofrance.fr',
  home: '/particuliers',
  calculateur: '/particuliers/calculer-mes-aides',
  aidesHub: '/particuliers/aides',
  travauxHub: '/particuliers/travaux',
  contact: '/contact?source=particulier',
  /** URL de fallback pour les liens FAQ "écrivez-nous" */
  contactCallback: '/contact?source=particulier&type=callback',
} as const;

/* ────────────────────────────────────────────────────────────────
 * 8. Types réutilisables (CTAs, bullets, KPI)
 * ──────────────────────────────────────────────────────────────── */

export type ParticulierCta = {
  label: string;
  href: string;
  /** Nom d'icône Lucide (cf. lib/lucide-icons) */
  iconName?: string;
};

export type ParticulierKpi = {
  /** Valeur affichée (string statique si pas de CountUp animée) */
  value: string;
  /** Valeur numérique optionnelle pour CountUp animée */
  numericValue?: number;
  /** Suffixe à coller à la valeur animée (ex. " €", "%") */
  suffix?: string;
  /** Label principal (1 ligne) */
  label: string;
  /** Source/note explicative (2e ligne, optionnel) */
  source?: string;
};

export type ParticulierKeyPoint = {
  iconName: string;
  /** Titre court (1 ligne max) */
  label: string;
  /** Description (2-3 lignes) */
  description: string;
};

export type ParticulierStep = {
  /** Titre du step */
  title: string;
  /** Description (2-4 lignes) */
  description: string;
  /** Optionnel : eyebrow custom au-dessus du titre (sinon "Étape XX") */
  eyebrow?: string;
};

export type ParticulierBreadcrumbItem = {
  name: string;
  /** Si défini, item cliquable ; sinon item courant en bold */
  href?: string;
};

/**
 * Slot droit dans le hero : soit un slot React arbitraire, soit une PromiseList auto.
 * (Conservé en types pour permettre l'auto-complétion dans les pages.)
 */
export type ParticulierHeroSide =
  | { kind: 'slot'; content: ReactNode }
  | {
      kind: 'promise-list';
      title?: string;
      subtitle?: string;
      items: Array<{
        label: string;
        value: string;
        tone?: 'brand' | 'accent' | 'ink' | 'stone';
      }>;
      footnote?: string;
    };

/* ────────────────────────────────────────────────────────────────
 * 9. Helpers builders
 * ──────────────────────────────────────────────────────────────── */

/**
 * Construit l'URL d'une page particulier à partir d'un chemin relatif.
 * Exemple : `pageUrl('aides/maprimerenov')` -> `https://solutionecofrance.fr/particuliers/aides/maprimerenov`
 */
export function particulierPageUrl(relativePath: string): string {
  const cleanPath = relativePath.replace(/^\//, '');
  return `${PARTICULIER_URLS.site}/particuliers/${cleanPath}`;
}

/**
 * Construit la liste de breadcrumb avec "Accueil" et "Particuliers" en tête.
 */
export function buildParticulierBreadcrumb(
  trailing: Array<{ name: string; href?: string }>,
): ParticulierBreadcrumbItem[] {
  return [
    { name: 'Accueil', href: '/' },
    { name: 'Particuliers', href: PARTICULIER_URLS.home },
    ...trailing,
  ];
}
